import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16 } from '@onefootprint/icons';
import type { ApiNodeDataRules, GraphOperationUpdateNode, NodeConfigurationContextRules } from '@onefootprint/request-types/dashboard';
import { Divider, Form, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import snakeCase from 'lodash/snakeCase';
import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetTraceContext } from '@/components/playbook-builder/hooks/use-get-trace-context';
import { getFiredRuleId } from '@/components/playbook-builder/utils/get-fired-rule-id';
import { getTraceHighlightStyles } from '@/components/playbook-builder/utils/get-trace-highlight-styles';
import type { ApplyEditMutation } from '../../../base-node';
import { CelNodeFormInstructions, CelNodeFormSubtitle } from '../../../shared/cel-node-form-instructions';
import { useRulesErrorHandler } from '../../hooks/use-rules-error-handler';
import { useRulesWarningMessages } from '../../hooks/use-rules-warning-messages';
import type { RulesFormData } from '../../rules-node.types';
import { newCondition, RulesGroup } from '../rules-group';
type RulesFormProps = {
  data: ApiNodeDataRules;
  nodeId: string;
  mutation: ApplyEditMutation;
  config: NodeConfigurationContextRules;
  canEdit: boolean;
};
export const RulesForm = t0 => {
  const $ = _c(117);
  const {
    data,
    nodeId,
    mutation,
    config,
    canEdit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rulesDataNode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    toggle
  } = useDirty();
  const trace = useGetTraceContext(nodeId, "rules");
  let t2;
  if ($[1] !== data.key || $[2] !== data.name) {
    t2 = data.key && data.key !== snakeCase(data.name);
    $[1] = data.key;
    $[2] = data.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(t2);
  const t3 = !canEdit;
  let t4;
  if ($[4] !== data.defaultValue || $[5] !== data.groups || $[6] !== data.key || $[7] !== data.name) {
    t4 = {
      name: data.name,
      key: data.key,
      groups: data.groups,
      defaultValue: data.defaultValue
    };
    $[4] = data.defaultValue;
    $[5] = data.groups;
    $[6] = data.key;
    $[7] = data.name;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = {
      disabled: t3,
      defaultValues: t4
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const form = useForm(t5);
  let t6;
  if ($[12] !== form.control) {
    t6 = {
      control: form.control,
      name: "groups"
    };
    $[12] = form.control;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const groups = useFieldArray(t6);
  let t7;
  if ($[14] !== form.control) {
    t7 = {
      control: form.control,
      name: "groups"
    };
    $[14] = form.control;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const formGroups = useWatch(t7);
  let t8;
  if ($[16] !== form.formState.isDirty || $[17] !== toggle) {
    t8 = () => {
      toggle("rules-form", form.formState.isDirty);
    };
    $[16] = form.formState.isDirty;
    $[17] = toggle;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== form.formState.isDirty) {
    t9 = [form.formState.isDirty];
    $[19] = form.formState.isDirty;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] !== trace) {
    t10 = trace?.context?.kind === "rules" && !getFiredRuleId(trace.context.groupResults);
    $[21] = trace;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const defaultExecuted = t10;
  const borderClasses = getTraceHighlightStyles(defaultExecuted);
  let t11;
  if ($[23] !== form || $[24] !== formGroups || $[25] !== nodeId) {
    t11 = {
      nodeId,
      form,
      formGroups
    };
    $[23] = form;
    $[24] = formGroups;
    $[25] = nodeId;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const {
    reapplyBackendErrors
  } = useRulesErrorHandler(t11);
  const warningMessages = useRulesWarningMessages(nodeId);
  let t12;
  if ($[27] !== mutation || $[28] !== nodeId) {
    t12 = async formData => {
      ;
      try {
        const operation = {
          kind: "update_node",
          nodeId,
          nodeData: {
            kind: "rules",
            name: formData.name,
            key: formData.key,
            groups: formData.groups,
            defaultValue: formData.defaultValue
          }
        };
        await mutation.mutateAsync(operation);
      } catch (t13) {
        const error = t13;
        console.error("Error updating rules node:", error);
      }
    };
    $[27] = mutation;
    $[28] = nodeId;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const handleFormSubmit = t12;
  let t13;
  if ($[30] !== groups) {
    t13 = () => {
      groups.append({
        conditions: [newCondition()],
        value: ""
      }, {
        shouldFocus: false
      });
    };
    $[30] = groups;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const onAddGroup = t13;
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setKeyManuallyEdited(true);
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const handleKeyChange = t14;
  let t15;
  if ($[33] !== form || $[34] !== keyManuallyEdited) {
    t15 = value => {
      if (!keyManuallyEdited) {
        form.setValue("key", snakeCase(value));
      }
    };
    $[33] = form;
    $[34] = keyManuallyEdited;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const handleNameChange = t15;
  let t16;
  if ($[36] !== reapplyBackendErrors) {
    t16 = () => {
      reapplyBackendErrors();
    };
    $[36] = reapplyBackendErrors;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const onInvalid = t16;
  const T0 = FormProvider;
  const t17 = "rules-form";
  let t18;
  if ($[38] !== form || $[39] !== handleFormSubmit || $[40] !== onInvalid) {
    t18 = form.handleSubmit(handleFormSubmit, onInvalid);
    $[38] = form;
    $[39] = handleFormSubmit;
    $[40] = onInvalid;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = "flex flex-col gap-4";
  let t20;
  if ($[42] !== t) {
    t20 = t(_temp);
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <div className="text-label-2">{t20}</div>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t(_temp2);
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== handleNameChange) {
    t23 = e => handleNameChange(e.target.value);
    $[48] = handleNameChange;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23) {
    t24 = <Form.Input name="name" placeholder={t22} size="compact" onChange={t23} />;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t21 || $[54] !== t24) {
    t25 = <div className="flex flex-col gap-2">{t21}{t24}</div>;
    $[53] = t21;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t(_temp3);
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <div className="text-label-2">{t26}</div>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = t(_temp4);
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t) {
    t29 = t(_temp5);
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t29) {
    t30 = {
      required: t29
    };
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t28 || $[67] !== t30) {
    t31 = <Form.Input name="key" addon="data." placeholder={t28} size="compact" rules={t30} onChange={handleKeyChange} />;
    $[66] = t28;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t27 || $[70] !== t31) {
    t32 = <div className="flex flex-col gap-2">{t27}{t31}</div>;
    $[69] = t27;
    $[70] = t31;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t25 || $[73] !== t32) {
    t33 = <div className="flex flex-col gap-5">{t25}{t32}</div>;
    $[72] = t25;
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Divider />;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <CelNodeFormSubtitle i18nKey={_temp6} />;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t) {
    t36 = t(_temp7);
    $[77] = t;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t) {
    t37 = t(_temp8);
    $[79] = t;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== t36 || $[82] !== t37) {
    t38 = <section className="flex flex-col">{t34}<div className="pt-4"><CelNodeFormInstructions subtitle={t35} celSubtitle={t36} instructionsLabel={t37} /></div></section>;
    $[81] = t36;
    $[82] = t37;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  const t39 = "flex flex-col";
  let t40;
  if ($[84] !== canEdit || $[85] !== config || $[86] !== groups || $[87] !== onAddGroup || $[88] !== t || $[89] !== trace || $[90] !== warningMessages) {
    t40 = groups.fields.map((group, index) => <div key={group.id} className="flex flex-col"><RulesGroup groupIndex={index} canEdit={canEdit} autocompleteItems={config.autocomplete} languageDefinition={config.languageDefinition} onRemoveGroup={() => groups.remove(index)} numGroups={groups.fields.length} onMoveGroup={(oldIndex, newIndex) => groups.move(oldIndex, newIndex)} trace={trace} warningMessages={warningMessages} /><div className="p-3 ml-3 border-l border-tertiary flex flex-row justify-between w-full"><span className="text-body-2 text-tertiary">{index < groups.fields.length - 1 ? t(_temp9) : t(_temp10)}</span>{index === groups.fields.length - 1 && <LinkButton type="button" onClick={onAddGroup} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={!canEdit}>{t(_temp11)}</LinkButton>}</div></div>);
    $[84] = canEdit;
    $[85] = config;
    $[86] = groups;
    $[87] = onAddGroup;
    $[88] = t;
    $[89] = trace;
    $[90] = warningMessages;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  const t41 = cx("rounded flex flex-col gap-3", borderClasses);
  let t42;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <span className="text-body-2 text-tertiary flex flex-row gap-1 whitespace-nowrap">Set value to</span>;
    $[92] = t42;
  } else {
    t42 = $[92];
  }
  let t43;
  if ($[93] !== t) {
    t43 = t(_temp12);
    $[93] = t;
    $[94] = t43;
  } else {
    t43 = $[94];
  }
  let t44;
  if ($[95] !== t) {
    t44 = t(_temp13);
    $[95] = t;
    $[96] = t44;
  } else {
    t44 = $[96];
  }
  let t45;
  if ($[97] !== t44) {
    t45 = {
      required: t44
    };
    $[97] = t44;
    $[98] = t45;
  } else {
    t45 = $[98];
  }
  let t46;
  if ($[99] !== t43 || $[100] !== t45) {
    t46 = <div className="flex flex-row bg-secondary p-2 items-center gap-3"><div className="flex flex-row items-center gap-2 w-full min-h-[32px]">{t42}<Form.Input name="defaultValue" placeholder={t43} size="compact" rules={t45} className="max-w-[200px]" /></div></div>;
    $[99] = t43;
    $[100] = t45;
    $[101] = t46;
  } else {
    t46 = $[101];
  }
  let t47;
  if ($[102] !== t41 || $[103] !== t46) {
    t47 = <div className={t41}>{t46}</div>;
    $[102] = t41;
    $[103] = t46;
    $[104] = t47;
  } else {
    t47 = $[104];
  }
  let t48;
  if ($[105] !== t40 || $[106] !== t47) {
    t48 = <div className={t39}>{t40}{t47}</div>;
    $[105] = t40;
    $[106] = t47;
    $[107] = t48;
  } else {
    t48 = $[107];
  }
  let t49;
  if ($[108] !== t18 || $[109] !== t33 || $[110] !== t38 || $[111] !== t48) {
    t49 = <form id={t17} onSubmit={t18} className={t19}>{t33}{t38}{t48}</form>;
    $[108] = t18;
    $[109] = t33;
    $[110] = t38;
    $[111] = t48;
    $[112] = t49;
  } else {
    t49 = $[112];
  }
  let t50;
  if ($[113] !== T0 || $[114] !== form || $[115] !== t49) {
    t50 = <T0 {...form}>{t49}</T0>;
    $[113] = T0;
    $[114] = form;
    $[115] = t49;
    $[116] = t50;
  } else {
    t50 = $[116];
  }
  return t50;
};
function _temp($) {
  return $.nodeName;
}
function _temp2($_0) {
  return $_0.nodeNamePlaceholder;
}
function _temp3($_1) {
  return $_1.setValueOf;
}
function _temp4($_2) {
  return $_2.keyPlaceholder;
}
function _temp5($_3) {
  return $_3.keyRequired;
}
function _temp6($_4) {
  return $_4.rulesDataNode.subtitle;
}
function _temp7($_5) {
  return $_5.celSubtitle;
}
function _temp8($_6) {
  return $_6.instructions;
}
function _temp9($_7) {
  return $_7.elseIf;
}
function _temp10($_8) {
  return $_8.else;
}
function _temp11($_9) {
  return $_9.addGroup;
}
function _temp12($_10) {
  return $_10.valuePlaceholder;
}
function _temp13($_11) {
  return $_11.defaultValueRequired;
}